import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const DeepLinkDepthContext = createContext(0);

export const DeepLinkDepthProvider = DeepLinkDepthContext.Provider;

export function useDeepLinkDepth() {
    return useContext(DeepLinkDepthContext);
}

function safeDecode(s) {
    try {
        return decodeURIComponent(s);
    } catch {
        return s;
    }
}

// Hash format: `#segment/segment?key=val&key=val`
function parseHash(hash) {
    const raw = hash.replace(/^#/, '');
    const qIndex = raw.indexOf('?');
    const pathPart = qIndex === -1 ? raw : raw.slice(0, qIndex);
    const queryPart = qIndex === -1 ? '' : raw.slice(qIndex + 1);

    const segments = pathPart.split('/').filter(Boolean).map(safeDecode);

    const params = {};
    if (queryPart) {
        for (const pair of queryPart.split('&')) {
            if (!pair) continue;
            const eq = pair.indexOf('=');
            const k = safeDecode(eq === -1 ? pair : pair.slice(0, eq));
            const v = eq === -1 ? '' : safeDecode(pair.slice(eq + 1));
            if (k) params[k] = v;
        }
    }

    return { segments, params };
}

function writeHash({ segments, params }) {
    const path = segments.map(encodeURIComponent).join('/');
    const keys = Object.keys(params).sort();
    const query = keys
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&');
    const body = query ? `${path}?${query}` : path;
    const hash = body ? `#${body}` : '';
    const url = window.location.pathname + window.location.search + hash;
    window.history.replaceState(null, '', url);
    window.dispatchEvent(new Event('hashchange'));
}

function useHashState() {
    const [state, setState] = useState(() => parseHash(window.location.hash));
    useEffect(() => {
        const handler = () => setState(parseHash(window.location.hash));
        window.addEventListener('hashchange', handler);
        return () => window.removeEventListener('hashchange', handler);
    }, []);
    return state;
}

export function useDeepLinkSegment(defaultKey) {
    const depth = useDeepLinkDepth();
    const { segments } = useHashState();

    const currentKey = segments[depth] ?? defaultKey;

    const setKey = useCallback(
        (newKey) => {
            const { segments: current, params } = parseHash(window.location.hash);
            const nextSegments = current.slice(0, depth);
            if (newKey != null && newKey !== defaultKey) {
                nextSegments.push(String(newKey));
            }
            writeHash({ segments: nextSegments, params });
        },
        [depth, defaultKey]
    );

    return [currentKey, setKey];
}

export function useDeepLinkParam(name, defaultValue) {
    const { params } = useHashState();

    const raw = params[name];
    const currentValue = raw === undefined ? defaultValue : raw;

    const setValue = useCallback(
        (newValue) => {
            const { segments, params: current } = parseHash(window.location.hash);
            const nextParams = { ...current };
            const isDefault =
                newValue == null ||
                newValue === defaultValue ||
                String(newValue) === String(defaultValue);
            if (isDefault) {
                delete nextParams[name];
            } else {
                nextParams[name] = String(newValue);
            }
            writeHash({ segments, params: nextParams });
        },
        [name, defaultValue]
    );

    return [currentValue, setValue];
}
